// ---------------------------------------------------------------------------
// Pricing Table Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface PricingTier {
  name: string;
  price: string;
  period?: string;
  features: string[];
  ctaText: string;
  ctaUrl?: string;
  /** Plan slug for Stripe checkout (e.g. "growth", "starter") */
  planSlug?: string;
  isPopular?: boolean;
  highlighted?: boolean;
}

interface PricingTableProps {
  heading?: string;
  tiers: PricingTier[];
  showToggle?: boolean;
  /** Enable Stripe checkout on CTA clicks (requires tenant Stripe setup) */
  stripeCheckout?: boolean;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  const p = props as unknown as PricingTableProps;

  const heading = p.heading ?? "Pricing";
  const tiers = Array.isArray(p.tiers) ? p.tiers : [];
  const showToggle = p.showToggle === true;

  const headingHtml = `<h2 class="ease-pricing-heading">${text(heading)}</h2>`;

  const toggleHtml = showToggle
    ? `\n  <div class="ease-pricing-toggle" role="group" aria-label="Billing period">
    <button type="button" class="ease-pricing-toggle-btn ease-pricing-toggle-btn--active" data-period="monthly">Monthly</button>
    <button type="button" class="ease-pricing-toggle-btn" data-period="annual">Annual</button>
  </div>`
    : "";

  const tiersHtml = tiers
    .map((tier) => {
      const isHighlighted = tier.isPopular || tier.highlighted;
      const highlightClass = isHighlighted ? " ease-pricing-tier--popular" : "";
      const badge = isHighlighted
        ? `\n      <span class="ease-pricing-badge">Most Popular</span>`
        : "";

      const periodHtml = tier.period
        ? `<span class="ease-pricing-period">/${text(tier.period)}</span>`
        : "";

      const featuresHtml = tier.features
        .map((f) => `        <li>${text(f)}</li>`)
        .join("\n");

      const ctaHref = url(tier.ctaUrl ?? "#form");
      const planAttr = tier.planSlug ? ` data-plan-slug="${attr(tier.planSlug)}"` : "";

      return `    <article class="ease-pricing-tier${highlightClass}" aria-label="${attr(tier.name)} plan">${badge}
      <h3 class="ease-pricing-tier-name">${text(tier.name)}</h3>
      <div class="ease-pricing-price">
        <span class="ease-pricing-amount">${text(tier.price)}</span>
        ${periodHtml}
      </div>
      <ul class="ease-pricing-features">
${featuresHtml}
      </ul>
      <a href="${ctaHref}" class="ease-btn ease-btn-primary ease-pricing-cta"${planAttr}>${text(tier.ctaText)}</a>
    </article>`;
    })
    .join("\n");

  // PB-T.24: Client-side checkout script for published pages
  // When stripeCheckout is enabled AND tiers have planSlug, clicking the CTA
  // calls POST /v1/billing/checkout → redirects to Stripe Checkout
  // C8 fix: derive API base URL from submissionEndpoint (e.g. "https://api.stero.ai/pages/submit")
  // Extract origin so checkout calls go to the correct API domain on custom-domain pages
  const apiBaseUrl = (() => {
    try { return new URL(ctx.submissionEndpoint).origin; } catch { return ""; }
  })();

  const hasCheckoutTiers = p.stripeCheckout && tiers.some((t) => t.planSlug);
  const checkoutScript = hasCheckoutTiers
    ? `\n<script>
(function(){
  var section = document.currentScript.previousElementSibling;
  if (!section) return;
  var apiBase = ${JSON.stringify(apiBaseUrl)};
  var period = 'monthly';
  var toggleBtns = section.querySelectorAll('.ease-pricing-toggle-btn');
  toggleBtns.forEach(function(btn) {
    btn.addEventListener('click', function() {
      period = btn.getAttribute('data-period') || 'monthly';
      toggleBtns.forEach(function(b) { b.classList.remove('ease-pricing-toggle-btn--active'); });
      btn.classList.add('ease-pricing-toggle-btn--active');
    });
  });
  section.querySelectorAll('.ease-pricing-cta[data-plan-slug]').forEach(function(cta) {
    cta.setAttribute('data-original-text', cta.textContent || 'Get Started');
    cta.addEventListener('click', function(e) {
      e.preventDefault();
      var slug = cta.getAttribute('data-plan-slug');
      if (!slug) return;
      cta.textContent = 'Loading...';
      cta.style.pointerEvents = 'none';
      fetch(apiBase + '/v1/billing/checkout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planKey: slug, billingInterval: period === 'annual' ? 'annual' : 'monthly' })
      })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.checkoutUrl) window.location.href = d.checkoutUrl;
        else throw new Error(d.error || 'Checkout failed');
      })
      .catch(function() {
        cta.textContent = cta.getAttribute('data-original-text') || 'Get Started';
        cta.style.pointerEvents = '';
        window.location.href = '/pricing?plan=' + slug;
      });
    });
  });
})();
</script>`
    : "";

  return `<section class="ease-section ease-pricing-table" aria-label="${attr(heading)}">
  ${headingHtml}${toggleHtml}
  <div class="ease-pricing-tiers" style="display: grid; grid-template-columns: repeat(${attr(String(tiers.length))}, 1fr); gap: var(--ease-spacing-lg, 2rem);">
${tiersHtml}
  </div>
</section>${checkoutScript}`;
}

registerRenderer("pricing-table", { render });
